import api from '@/lib/api'

// =========================================================================
// Types
// =========================================================================

export interface RevenueStats {
    mrr: number
    arr: number
    activePaid: number
    trialing: number
    trialConversionRate: number
    churnRate: number
    arpu: number
    ltvEstimate: number
    downgradeOffersClaimed: number
    periodComparison?: {
        mrrChange: string
        arrChange: string
        trialConversionChange: string
        churnChange: string
        arpuChange: string
    }
}

export interface PlanPriceRow {
    /** Monthly equivalent (annual / 12); null when the agency's Stripe price is not on record. */
    monthlyAmount: number | null
    isCurrent: boolean
    agencyCount: number
    activeCount: number
    revenue: number
}

export interface PlanDistributionItem {
    planId: number | null
    planName: string
    displayName: string
    billingInterval: 'free' | 'monthly' | 'yearly' | 'none'
    agencyCount: number
    revenueContribution: number
    /** plans.is_active = false: kept out of the public plan list. Not retired: Agency Mini is live but hidden. */
    isRetired: boolean
    listMonthlyPrice: number | null
    currentMonthlyAmount: number | null
    prices: PlanPriceRow[]
}

/** One agency behind a Plan Distribution price row. */
export interface PlanPriceAgency {
    id: string
    name: string
    stripeStatus: string | null
    stripePriceId: string | null
    /** What it adds to MRR: its monthly price when active, else 0. */
    mrr: number
}

export interface ChurnDataItem {
    reason: string
    count: number
}

export interface MrrTrendPoint {
    date: string
    name: string
    fullDate: string
    mrr: number
}

export interface TrialExpiryItem {
    id: string
    name: string
    plan: string
    trialEndDate: string
    daysRemaining: number
}



// =========================================================================
// Service
// =========================================================================

export const RevenueService = {
    getStats: async (range: string = '1m') => {
        const response = await api.get('/admin/revenue/stats', { params: { range } })
        return response.data.data as RevenueStats
    },

    getPlanDistribution: async () => {
        const response = await api.get('/admin/revenue/plan-distribution')
        // Defaults keep the card working against a backend deployed before the price rows.
        return (response.data.data as Partial<PlanDistributionItem>[]).map((item) => ({
            ...item,
            isRetired: item.isRetired ?? false,
            listMonthlyPrice: item.listMonthlyPrice ?? null,
            currentMonthlyAmount: item.currentMonthlyAmount ?? null,
            prices: item.prices ?? [],
        })) as PlanDistributionItem[]
    },

    /** monthlyAmount null is the "Price not on record" row. */
    getPlanPriceAgencies: async (planId: number, monthlyAmount: number | null) => {
        const response = await api.get('/admin/revenue/plan-distribution/agencies', {
            params: { planId, amount: monthlyAmount ?? 'none' },
        })
        return response.data.data as PlanPriceAgency[]
    },

    getChurnAnalytics: async (range: string = '1m') => {
        const response = await api.get('/admin/revenue/churn', { params: { range } })
        return response.data.data as ChurnDataItem[]
    },

    getMrrTrend: async (range: string = '1m', userTimezone?: string) => {
        const response = await api.get('/admin/revenue/mrr-trend', { params: { range } })

        const tz = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

        const data: MrrTrendPoint[] = response.data.data.map((item: any) => {
            const date = new Date(item.date)
            const name = date.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' })
            const fullDate = date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            return {
                date: item.date,
                name,
                fullDate,
                mrr: Number(item.mrr || 0),
            }
        })

        return data
    },

    getTrialExpiry: async () => {
        const response = await api.get('/admin/revenue/trial-expiry')
        return response.data.data as TrialExpiryItem[]
    },

}
